import dynamic from 'next/dynamic';
import React from 'react';
import { AddNoteModalProps } from '~/components/AddNoteModal/AddNoteModal';

import { SavedListsModalProps } from '../SavedListsModal/SavedListsModal.types';
import { useCreatorActionModal } from './CreatorActionModal.context';
import type { ConfirmTalentRemovalModalProps } from './components/ConfirmTalentRemoval';

const enum CreatorActionModalTypes {
  addToList = 'addToList',
  removeFromList = 'removeFromList',
  editNote = 'editNote',
}

export type CreatorActionModalProps = {
  actions: {
    [CreatorActionModalTypes.addToList]?: Partial<SavedListsModalProps>;
    [CreatorActionModalTypes.removeFromList]?: Partial<ConfirmTalentRemovalModalProps>;
    [CreatorActionModalTypes.editNote]?: Partial<AddNoteModalProps>;
  };
};

const DynamicAddToListModal = dynamic(
  () =>
    import('../SavedListsModal').then(({ SavedListsModal }) => ({
      default: SavedListsModal,
    })),
  {
    ssr: false,
  },
);

const DynamicConfirmTalentRemovalModal = dynamic(
  () =>
    import('./components/ConfirmTalentRemoval').then(
      ({ ConfirmTalentRemovalModal }) => ({
        default: ConfirmTalentRemovalModal,
      }),
    ),
  {
    ssr: false,
  },
);

const DynamicAddNoteModal = dynamic(
  () =>
    import('../AddNoteModal/AddNoteModal').then(({ AddNoteModal }) => ({
      default: AddNoteModal,
    })),
  {
    ssr: false,
  },
);

export const CreatorActionModal: React.FC<CreatorActionModalProps> = ({
  actions,
}) => {
  const {
    isAddToListModalOpen,
    isRemoveFromListModalOpen,
    setCreators,
    setIsAddToListModalOpen,
    setIsRemoveFromListModalOpen,
    isEditNoteModalOpen,
    setIsEditNoteModalOpen,
  } = useCreatorActionModal();

  const handleAddToListClose = () => {
    setIsAddToListModalOpen(false);
    // TODO: Remove this once unified in one store
    setCreators([]);
  };

  const handleRemoveFromListClose = () => {
    setIsRemoveFromListModalOpen(false);
    // TODO: Remove this once unified in one store
    setCreators([]);
  };

  const handleEditNoteClose = () => {
    setIsEditNoteModalOpen(false);
    // TODO: Remove this once unified in one store
    setCreators([]);
  };

  return (
    <>
      {Object.entries(actions).map(([actionType, props]) => {
        if (actionType === CreatorActionModalTypes.addToList && props) {
          const { isOpen, onClose, ...restProps } = props;
          return (
            <DynamicAddToListModal
              key={actionType}
              isOpen={isOpen ?? isAddToListModalOpen}
              onClose={onClose ?? handleAddToListClose}
              {...(restProps as Omit<
                SavedListsModalProps,
                'isOpen' | 'onClose'
              >)}
            />
          );
        }
        if (actionType === CreatorActionModalTypes.removeFromList && props) {
          const { isOpen, onClose, ...restProps } = props;
          return (
            <DynamicConfirmTalentRemovalModal
              key={actionType}
              isOpen={isOpen ?? isRemoveFromListModalOpen}
              onClose={onClose ?? handleRemoveFromListClose}
              {...(restProps as Omit<
                ConfirmTalentRemovalModalProps,
                'isOpen' | 'onClose'
              >)}
            />
          );
        }
        if (actionType === CreatorActionModalTypes.editNote && props) {
          const { isOpen, onClose, ...restProps } = props;
          return (
            <DynamicAddNoteModal
              key={actionType}
              isOpen={isOpen ?? isEditNoteModalOpen}
              onClose={onClose ?? handleEditNoteClose}
              {...(restProps as Omit<AddNoteModalProps, 'isOpen' | 'onClose'>)}
            />
          );
        }
        return null;
      })}
    </>
  );
};
